/**
 * DOH Medication Selection Step - Redesigned
 * 
 * Addresses Client Feedback:
 * - Only shows DOH-approved medications
 * - Removes quantity and duration (moved to next step)
 * - Clean, focused medication search and selection
 * - Real-time safety validation
 * - Drug interaction checking
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Card,
    Row,
    Col,
    Input,
    Table,
    Button,
    Alert,
    Space,
    Tag,
    Badge,
    Modal,
    message,
    Spin,
    Empty,
    Tooltip,
    Divider,
    AutoComplete,
    Form
} from 'antd';
import {
    MedicineBoxOutlined,
    SearchOutlined,
    PlusOutlined,
    DeleteOutlined,
    SafetyCertificateOutlined,
    ExclamationCircleOutlined,
    CheckCircleOutlined,
    InfoCircleOutlined,
    EditOutlined
} from '@ant-design/icons';

import medicationService from '../../../services/medicationService';
import medpassService from '../../../services/medpassService';


const DOHMedicationSelectionStep = ({ patient, workflowData, onMedicationsSelected, loading }) => {
    const [searchResults, setSearchResults] = useState([]);
    const [selectedMedications, setSelectedMedications] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const debounceTimer = useRef(null);
    const currentRequest = useRef(null);
    const [safetyChecks, setSafetyChecks] = useState({
        drugInteractions: { status: 'pending', alerts: [] },
        allergies: { status: 'pending', alerts: [] },
        contraindications: { status: 'pending', alerts: [] }
    });
    const [showSafetyModal, setShowSafetyModal] = useState(false);
    // Route customization during selection removed per request
    
    // Common route suggestions for autocomplete
    const commonRoutes = [
        'oral', 'intravenous', 'intramuscular', 'subcutaneous',
        'topical', 'inhalation', 'rectal', 'vaginal',
        'sublingual', 'buccal', 'transdermal', 'nasal',
        'ophthalmic', 'otic', 'intradermal', 'epidural',
        'intrathecal', 'intraperitoneal', 'intra-articular'
    ];

    // Initialize with existing selections
    useEffect(() => {
        if (workflowData?.selected_medications?.length > 0) {
            setSelectedMedications(workflowData.selected_medications);
        }
    }, [workflowData]);

    // Cleanup timers on unmount
    useEffect(() => {
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
            if (currentRequest.current) {
                currentRequest.current.abort();
            }
        };
    }, []);

    // Run safety checks when medications change
    useEffect(() => {
        if (selectedMedications.length > 0) {
            runSafetyValidation();
        } else {
            resetSafetyChecks();
        }
    }, [selectedMedications]);

    const resetSafetyChecks = () => {
        setSafetyChecks({
            drugInteractions: { status: 'pending', alerts: [] },
            allergies: { status: 'pending', alerts: [] },
            contraindications: { status: 'pending', alerts: [] }
        });
    };

    const searchDOHMedications = useCallback(async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2) {
            setSearchResults([]);
            return;
        }

        if (currentRequest.current) {
            currentRequest.current.abort();
        }

        setSearchLoading(true);
        try {
            currentRequest.current = new AbortController();
            
            const result = await medicationService.typeaheadDOHApprovedMedications({
                q: searchTerm,
                patient_id: patient.id,
                limit: 10
            });

            if (result.success) {
                setSearchResults(result.data);
            } else {
                message.error('Failed to search medications');
                setSearchResults([]);
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Error searching DOH medications:', error);
                message.error('Error searching medications');
                setSearchResults([]);
            }
        } finally {
            setSearchLoading(false);
        }
    }, [patient.id]);

    const debouncedSearch = useCallback((searchTerm) => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
        
        debounceTimer.current = setTimeout(() => {
            searchDOHMedications(searchTerm);
        }, 300);
    }, [searchDOHMedications]);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);
        
        if (value.length === 0) {
            setSearchResults([]);
        } else if (value.length >= 2) {
            debouncedSearch(value);
        }
    };

    const addMedication = async (medication) => {
        // Check if medication is already selected
        const isAlreadySelected = selectedMedications.some(
            med => med.medication_id === (medication.medication_id || medication.id)
        );

        if (isAlreadySelected) {
            message.warning('Medication is already selected');
            return;
        }

        // Use only default route in selection step
        const defaultRoute = medication.route_of_administration?.[0] || 'oral';
        
        // Create medication selection object
        const medicationSelection = {
            medication_id: medication.medication_id || medication.id,
            drug_code: medication.drug_code,
            brand_name: medication.brand_name || (medication.text ? medication.text.split('(')[0].trim() : ''),
            generic_name: medication.generic_name,
            strength: medication.strengths?.[0] || (medication.strength ? { value: medication.strength, unit: '' } : { value: '', unit: '' }),
            dosage_form: medication.dosage_form || medication.form,
            route_of_administration: defaultRoute,
            custom_route_used: false,
            therapeutic_class: medication.therapeutic_class || medication.drug_class,
            doh_approved: true,
            doh_approval_date: medication.doh_approval_date,
            selected_at: new Date().toISOString()
        };

        const updatedSelections = [...selectedMedications, medicationSelection];
        setSelectedMedications(updatedSelections);

        message.success(`${medicationSelection.brand_name} added to prescription`);
    };

    const handleCustomizeRoute = () => {};

    const handleRouteUpdate = () => {};

    const updateSelectedMedicationRoute = () => {};

    const removeMedication = (medicationId) => {
        const updatedSelections = selectedMedications.filter(
            med => med.medication_id !== medicationId
        );
        setSelectedMedications(updatedSelections);
        message.success('Medication removed from prescription');
    };

    const runSafetyValidation = async () => {
        try {
            setSafetyChecks(prev => ({
                drugInteractions: { ...prev.drugInteractions, status: 'checking' },
                allergies: { ...prev.allergies, status: 'checking' },
                contraindications: { ...prev.contraindications, status: 'checking' }
            }));

            // Run comprehensive safety checks
            const safetyResult = await medpassService.runMedicationSafetyChecks({
                patient_id: patient.id,
                selected_medications: selectedMedications,
                patient_allergies: patient.allergies || [],
                current_medications: patient.current_medications || [],
                medical_conditions: patient.medical_conditions || []
            });

            if (safetyResult.success) {
                const { drug_interactions, allergy_conflicts, contraindications } = safetyResult.data;

                setSafetyChecks({
                    drugInteractions: {
                        status: drug_interactions.length > 0 ? 'warning' : 'clear',
                        alerts: drug_interactions
                    },
                    allergies: {
                        status: allergy_conflicts.length > 0 ? 'error' : 'clear',
                        alerts: allergy_conflicts
                    },
                    contraindications: {
                        status: contraindications.length > 0 ? 'warning' : 'clear',
                        alerts: contraindications
                    }
                });

                // Show modal if there are critical alerts
                const criticalAlerts = allergy_conflicts.length +
                    drug_interactions.filter(di => di.severity === 'severe').length;

                if (criticalAlerts > 0) {
                    setShowSafetyModal(true);
                }
            }
        } catch (error) {
            console.error('Error running safety checks:', error);
            message.error('Failed to run safety validation');
        }
    };

    const handleProceedToNext = () => {
        if (selectedMedications.length === 0) {
            message.error('Please select at least one medication');
            return;
        }

        // Check for critical safety issues
        const hasAllergyConflicts = safetyChecks.allergies.alerts.length > 0;
        const hasSevereDrugInteractions = safetyChecks.drugInteractions.alerts.some(
            alert => alert.severity === 'severe'
        );

        if (hasAllergyConflicts || hasSevereDrugInteractions) {
            Modal.confirm({
                title: 'Critical Safety Alerts Detected',
                content: 'There are critical safety concerns with the selected medications. Do you want to review them before proceeding?',
                icon: <ExclamationCircleOutlined />,
                okText: 'Review Alerts',
                cancelText: 'Proceed Anyway',
                onOk: () => setShowSafetyModal(true),
                onCancel: () => onMedicationsSelected(selectedMedications)
            });
        } else {
            onMedicationsSelected(selectedMedications);
        }
    };

    // Render search results table
    const renderSearchResults = () => {
        const columns = [
            {
                title: 'Medication',
                key: 'medication',
                render: (_, record) => (
                    <div>
                        <div style={{ fontWeight: 'bold' }}>{record.brand_name || record.text}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                            {record.generic_name}
                        </div>
                        <Tag color="blue" size="small">DOH Approved</Tag>
                    </div>
                )
            },
            {
                title: 'Strength',
                key: 'strength',
                render: (_, record) => (
                    <div>
                        {record.strengths?.map((strength, index) => (
                            <Tag key={index} size="small">
                                {strength.value}{strength.unit}
                            </Tag>
                        )) || (
                            record.strength && (
                                <Tag size="small">
                                    {record.strength}
                                </Tag>
                            )
                        )}
                    </div>
                )
            },
            {
                title: 'Form',
                key: 'form',
                render: (_, record) => (
                    <Tag color="green" size="small">
                        {(record.dosage_form || record.form)?.charAt(0).toUpperCase() + (record.dosage_form || record.form)?.slice(1)}
                    </Tag>
                )
            },
            {
                title: 'Route',
                key: 'route',
                render: (_, record) => (
                    <Tag color="blue" size="small">
                        {(record.route_of_administration?.[0] || 'oral').charAt(0).toUpperCase() + (record.route_of_administration?.[0] || 'oral').slice(1)}
                    </Tag>
                )
            },
            {
                title: 'Therapeutic Class',
                dataIndex: 'therapeutic_class',
                key: 'therapeutic_class',
                ellipsis: true
            },
            {
                title: 'Action',
                key: 'action',
                render: (_, record) => {
                    const isSelected = selectedMedications.some(
                        med => med.medication_id === record.medication_id
                    );

                    return (
                        <Button
                            type="primary"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => addMedication(record)}
                            disabled={isSelected}
                        >
                            {isSelected ? 'Selected' : 'Add'}
                        </Button>
                    );
                }
            }
        ];

        return (
            <Table
                columns={columns}
                dataSource={searchResults}
                rowKey={(record) => record.medication_id || record.id}
                size="small"
                pagination={false}
                scroll={{ y: 300 }}
                loading={searchLoading}
                locale={{
                    emptyText: searchLoading ? <Spin /> : (
                        <Empty
                            description={searchQuery.length === 0 
                                ? "Search for DOH-approved medications above" 
                                : searchQuery.length < 2 
                                ? "Type at least 2 characters to search"
                                : "No medications found"
                            }
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    )
                }}
            />
        );
    };

    // Render selected medications
    const renderSelectedMedications = () => {
        if (selectedMedications.length === 0) {
            return (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <MedicineBoxOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                    <div style={{ marginTop: 16, color: '#999' }}>
                        No medications selected yet
                    </div>
                    <div style={{ marginTop: 8, color: '#999', fontSize: '14px' }}>
                        Search and select DOH-approved medications above
                    </div>
                </div>
            );
        }

        const columns = [
            {
                title: 'Selected Medication',
                key: 'medication',
                render: (_, record) => (
                    <div>
                        <div style={{ fontWeight: 'bold' }}>{record.brand_name}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                            {record.generic_name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                            {record.strength.value}{record.strength.unit} {record.dosage_form}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                            Route: 
                            <Tag 
                                color={record.custom_route_used ? "purple" : "blue"} 
                                size="small" 
                                style={{ marginLeft: 4 }}
                            >
                                {record.route_of_administration}
                                {record.custom_route_used && " (custom)"}
                            </Tag>
                        </div>
                    </div>
                )
            },
            {
                title: 'Therapeutic Class',
                dataIndex: 'therapeutic_class',
                key: 'therapeutic_class',
                ellipsis: true
            },
            {
                title: 'DOH Status',
                key: 'doh_status',
                render: () => (
                    <Tag color="green" icon={<CheckCircleOutlined />}>
                        DOH Approved
                    </Tag>
                )
            },
            {
                title: 'Action',
                key: 'action',
                render: (_, record) => (
                    <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => removeMedication(record.medication_id)}
                    >
                        Remove
                    </Button>
                )
            }
        ];

        return (
            <Table
                columns={columns}
                dataSource={selectedMedications}
                rowKey="medication_id"
                size="small"
                pagination={false}
            />
        );
    };

    // Render safety status
    const renderSafetyStatus = () => {
        const getStatusIcon = (status) => {
            switch (status) {
                case 'clear': return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
                case 'warning': return <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />;
                case 'error': return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
                case 'checking': return <Spin size="small" />;
                default: return <InfoCircleOutlined style={{ color: '#d9d9d9' }} />;
            }
        };

        const getStatusText = (check, alerts) => {
            if (check.status === 'checking') return 'Checking...';
            if (check.status === 'clear') return 'No issues';
            return `${alerts.length} alert(s)`;
        };

        return (
            <Card
                title={
                    <Space>
                        <SafetyCertificateOutlined />
                        Safety Validation Status
                    </Space>
                }
                size="small"
                extra={
                    selectedMedications.length > 0 && (
                        <Button
                            size="small"
                            onClick={() => setShowSafetyModal(true)}
                        >
                            View Details
                        </Button>
                    )
                }
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Drug Interactions:</span>
                        <Space>
                            {getStatusIcon(safetyChecks.drugInteractions.status)}
                            <span>{getStatusText(safetyChecks.drugInteractions, safetyChecks.drugInteractions.alerts)}</span>
                        </Space>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Allergy Check:</span>
                        <Space>
                            {getStatusIcon(safetyChecks.allergies.status)}
                            <span>{getStatusText(safetyChecks.allergies, safetyChecks.allergies.alerts)}</span>
                        </Space>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Contraindications:</span>
                        <Space>
                            {getStatusIcon(safetyChecks.contraindications.status)}
                            <span>{getStatusText(safetyChecks.contraindications, safetyChecks.contraindications.alerts)}</span>
                        </Space>
                    </div>
                </Space>
            </Card>
        );
    };

    // Render safety alerts modal
    const renderSafetyModal = () => (
        <Modal
            title="Safety Validation Results"
            open={showSafetyModal}
            onCancel={() => setShowSafetyModal(false)}
            footer={[
                <Button key="close" onClick={() => setShowSafetyModal(false)}>
                    Close
                </Button>
            ]}
            width={800}
        >
            <Space direction="vertical" style={{ width: '100%' }}>
                {/* Allergy Alerts */}
                {safetyChecks.allergies.alerts.length > 0 && (
                    <Alert
                        message="Allergy Conflicts Detected"
                        description={
                            <ul>
                                {safetyChecks.allergies.alerts.map((alert, index) => (
                                    <li key={index}>{alert.message}</li>
                                ))}
                            </ul>
                        }
                        type="error"
                        showIcon
                    />
                )}

                {/* Drug Interaction Alerts */}
                {safetyChecks.drugInteractions.alerts.length > 0 && (
                    <Alert
                        message="Drug Interactions Detected"
                        description={
                            <ul>
                                {safetyChecks.drugInteractions.alerts.map((alert, index) => (
                                    <li key={index}>
                                        <Badge
                                            status={alert.severity === 'severe' ? 'error' : 'warning'}
                                            text={alert.message}
                                        />
                                    </li>
                                ))}
                            </ul>
                        }
                        type="warning"
                        showIcon
                    />
                )}

                {/* Contraindication Alerts */}
                {safetyChecks.contraindications.alerts.length > 0 && (
                    <Alert
                        message="Contraindications Detected"
                        description={
                            <ul>
                                {safetyChecks.contraindications.alerts.map((alert, index) => (
                                    <li key={index}>{alert.message}</li>
                                ))}
                            </ul>
                        }
                        type="warning"
                        showIcon
                    />
                )}
            </Space>
        </Modal>
    );

    return (
        <div className="doh-medication-selection-step">
            <Row gutter={[16, 16]}>
                {/* Search Section */}
                <Col xs={24} lg={16}>
                    <Card
                        title={
                            <Space>
                                <MedicineBoxOutlined />
                                DOH-Approved Medication Search
                            </Space>
                        }
                    >
                        <Alert
                            message="DOH-Approved Medications Only"
                            description="Only medications approved by the Department of Health (DOH) are available for selection. Dosing details will be configured in the next step."
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />

                        <Input
                            placeholder="Search by medication name, generic name, or therapeutic class (min 2 characters)"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            size="large"
                            prefix={<SearchOutlined />}
                            allowClear
                            onClear={() => {
                                setSearchQuery('');
                                setSearchResults([]);
                            }}
                        />
                        {searchQuery.length > 0 && searchQuery.length < 2 && (
                            <div style={{ fontSize: '12px', color: '#999', marginTop: 4 }}>
                                Type at least 2 characters to start searching...
                            </div>
                        )}

                        <Divider />

                        {renderSearchResults()}
                    </Card>
                </Col>

                {/* Safety Status */}
                <Col xs={24} lg={8}>
                    {renderSafetyStatus()}
                </Col>
            </Row>

            {/* Selected Medications */}
            <Row style={{ marginTop: 16 }}>
                <Col xs={24}>
                    <Card
                        title={`Selected Medications (${selectedMedications.length})`}
                        extra={
                            selectedMedications.length > 0 && (
                                <Tag color="blue">
                                    Ready for dosing configuration
                                </Tag>
                            )
                        }
                    >
                        {renderSelectedMedications()}
                    </Card>
                </Col>
            </Row>

            {/* Action Buttons */}
            <Row justify="end" style={{ marginTop: 24 }}>
                <Col>
                    <Button
                        type="primary"
                        size="large"
                        disabled={selectedMedications.length === 0}
                        loading={loading}
                        onClick={handleProceedToNext}
                    >
                        Proceed to Dosing & Instructions ({selectedMedications.length} medication{selectedMedications.length !== 1 ? 's' : ''})
                    </Button>
                </Col>
            </Row>

            {/* Safety Modal */}
            {renderSafetyModal()}
            
            {/* Route customization removed */}
        </div>
    );
};

export default DOHMedicationSelectionStep;